// Local cache of server-derived lists (friends, friend requests, blocks) so a
// cold start with the backend unreachable still renders the last
// known data instead of empty screens (offline-first; prime directive:
// Connection > everything — censorship/outage is the design case, not the edge).
//
// Same pattern as `favorites.ts`: synchronous localStorage so the very first
// render can hydrate from it (no flash of empty), an in-memory snapshot for
// referential stability (`useSyncExternalStore`-friendly), and a `reset…` test
// hook. Keys are versioned (`merkury.cache.v1.*`) so a future shape change
// invalidates cleanly by bumping the version; reads are additionally validated
// against the @merkury/protocol zod schemas — never re-defined here — so a
// corrupt or foreign value degrades to "no cache", not a crash.
//
// Presence is intentionally NOT trusted from disk: a stale cache must never
// show someone as online, so `online` is forced to false at write time and the
// snapshot only ever claims offline/unknown. Live fetches keep their real
// flags in component state; the cache is a boot fallback only.
//
// Nothing secret lives here: names, UUIDs, call metadata — the same data the
// server already stores for this account. Identity keys and tokens stay in the
// Keystore-backed kv/SecretStore.

import { z } from 'zod';
import { Friend, FriendRequest } from '@merkury/protocol';

const PREFIX = 'merkury.cache.v1.';

export interface CachedSnapshot<T> {
  data: T;
  /** ISO timestamp of the successful fetch this snapshot came from. */
  fetchedAt: string;
}

export interface CachedRequests {
  incoming: FriendRequest[];
  outgoing: FriendRequest[];
}

type Listener = () => void;
const listeners = new Set<Listener>();

function ls(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    // Storage can throw (disabled cookies, sandboxed iframe) — treat as absent.
    return undefined;
  }
}

function notify(): void {
  for (const listener of listeners) listener();
}

/** Subscribe to any cache-slot change (for `useSyncExternalStore`). */
export function subscribeServerCache(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// One slot per cached list. `memo` is the stable snapshot: undefined = not yet
// read from storage, null = read and absent/invalid.
class Slot<T> {
  private memo: CachedSnapshot<T> | null | undefined;

  constructor(
    readonly key: string,
    private readonly schema: z.ZodType<T>,
  ) {}

  get(): CachedSnapshot<T> | undefined {
    if (this.memo !== undefined) return this.memo ?? undefined;
    this.memo = this.load();
    return this.memo ?? undefined;
  }

  private load(): CachedSnapshot<T> | null {
    const raw = ls()?.getItem(PREFIX + this.key);
    if (!raw) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null) return null;
      const { data, fetchedAt } = parsed as { data?: unknown; fetchedAt?: unknown };
      if (typeof fetchedAt !== 'string') return null;
      const checked = this.schema.safeParse(data);
      return checked.success ? { data: checked.data, fetchedAt } : null;
    } catch {
      return null;
    }
  }

  set(data: T): void {
    this.memo = { data, fetchedAt: new Date().toISOString() };
    try {
      ls()?.setItem(PREFIX + this.key, JSON.stringify(this.memo));
    } catch {
      // Quota/serialization failure — the in-memory snapshot still serves this
      // session; worst case the next cold start falls back to empty.
    }
    notify();
  }

  clear(): void {
    this.memo = undefined;
    ls()?.removeItem(PREFIX + this.key);
  }

  /** Test seam: drop the in-memory snapshot so the next read re-hydrates. */
  reset(): void {
    this.memo = undefined;
  }
}

const friendsSlot = new Slot<Friend[]>('friends', z.array(Friend));
const requestsSlot = new Slot<CachedRequests>(
  'requests',
  z.object({ incoming: z.array(FriendRequest), outgoing: z.array(FriendRequest) }),
);
const blocksSlot = new Slot<string[]>('blocks', z.array(z.string()));

const ALL_SLOTS = [friendsSlot, requestsSlot, blocksSlot] as const;

// --- friends -----------------------------------------------------------------

export function getCachedFriends(): CachedSnapshot<Friend[]> | undefined {
  return friendsSlot.get();
}

/** Presence is degraded to offline at write time — see the module comment. */
export function setCachedFriends(friends: Friend[]): void {
  friendsSlot.set(friends.map((f) => ({ ...f, online: false })));
}

// --- friend requests (visibility only; accept/decline still need the server) ----

export function getCachedRequests(): CachedSnapshot<CachedRequests> | undefined {
  return requestsSlot.get();
}

export function setCachedRequests(requests: CachedRequests): void {
  requestsSlot.set(requests);
}

// --- blocks ----------------------------------------------------------------------

export function getCachedBlocks(): CachedSnapshot<string[]> | undefined {
  return blocksSlot.get();
}

export function setCachedBlocks(blocked: string[]): void {
  blocksSlot.set(blocked);
}

// --- lifecycle -----------------------------------------------------------------

/** Wipe every cached list — sign-out must not leave the previous account's data. */
export function clearServerCache(): void {
  for (const slot of ALL_SLOTS) slot.clear();
  notify();
}

/** Test seam: drop all in-memory snapshots so the next reads re-hydrate. */
export function resetServerCacheSnapshots(): void {
  for (const slot of ALL_SLOTS) slot.reset();
}
